<template>
  <v-container class="text-left" fluid>
    <v-row>
      <v-col cols="12" sm="4">
        <v-card flat style="background-color: transparent">
          <v-card-actions>
            <v-text-field
              style="margin-top: 2px !important"
              class="search-box"
              placeholder="Search Users By Name Or Email"
              prepend-icon="fa-search"
              v-model="search"
              type="text"
              clearable
              clear-icon="fa-times"
              @click:clear="resetSearch"
              @keyup="applySearch"
            ></v-text-field>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="12">
        <v-skeleton-loader v-if="loading && isTabletOrDesktop" type="table" :types="{ 'table-heading': '' }"></v-skeleton-loader>
        <v-skeleton-loader v-if="loading && !isTabletOrDesktop" type="table" :types="{ 'table-heading': '', 'table-thead' : 'heading@1' ,'table-row':'text@1'}" class="slider-table"></v-skeleton-loader>
        <v-data-table
          :headers="headers"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          v-show="!loading"
          :items="users" class="company-users-table"
        >
          <template v-slot:item.confirmed="{ item }">
            <v-icon v-if="item.confirmed" color="primary">fa-check</v-icon>
            <v-icon v-else color="error">fa-times</v-icon>
          </template>
           <template v-slot:item.email="{ item }">
            <span class="company-email">{{ item.email }}</span>
          </template>
          <template v-slot:item.id="{ item }">
            <!-- Direct only for now: the underlying send-welcome-email endpoint
                 is tenant-gated on the Direct API but NOT on the Hub API, where
                 the correct gate has to allow partner users to reach the
                 companies under their partner. Surfacing the button on the Hub
                 build before that gate exists would advertise a cross-tenant
                 hole. Drop the v-if once Hub's gate lands. -->
            <v-tooltip bottom v-if="$globalConfig.APP_NAME == 'direct'">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  outlined
                  small
                  color="secondary"
                  @click.prevent="sendWelcomeEmail(item.id)"
                >
                  <v-icon small>fa-paper-plane</v-icon>
                </v-btn>
              </template>
              <span>Send welcome email</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  :id="item.id"
                  v-on="on"
                  outlined
                  small
                  color="secondary"
                  class="ml-1"
                  @click.prevent="showEditPanel(item.id)"
                >
                  <v-icon small>fa-pencil-alt</v-icon>
                </v-btn>
              </template>
              <span>Edit User</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import superAdminAPI from '@/api/superadmin.js';
import userAPI from '@/api/user.js';
import LayoutBasic from '@/layouts/Dashboard';
import EditUser from '@/components/superadmin/users/Edit';
import CreateUser from '@/components/admin/users/Create';
import ViewDetails from '@/components/admin/users/View';
export default {
  name: 'SuperAdminCompanyUsers',
  components: {
    LayoutBasic,
  },
  data() {
    return {
      headers: [
        { text: 'First Name', value: 'first_name' },
        { text: 'Surname', value: 'last_name' },
        { text: 'Email', value: 'email' },
        { text: 'Last Login', value: 'last_login', sortable: false },
        //{ text: "Courses", value: "total_courses", sortable: false },
        //{ text: "Groups", value: "total_groups", sortable: false },
        //{ text: "Playlists", value: "total_channels", sortable: false },
        { text: '', value: 'id', align: 'end' },
      ],
      companyId: this.$route.params.id,
      users: [],
      search: '',
      timer: null,
      fetchTimer: null,
      searchParams: '',
      pagination: {},
      options: {},
      loading: false,
      confirmDelete: 'Are you sure you wish to delete this user?',
      confirmWelcomeEmail:
        'Send a welcome email to this user? They will receive a link to set their password.',
    };
  },
  computed: {
    serverItemsLength: function () {
      return this.pagination.total;
    },
    itemsPerPage: function () {
      return this.pagination.per_page;
    },
    page: function () {
      return this.pagination.current_page;
    },
    params: function () {
      return this.$gf.getQueryString(this.options, this.serverItemsLength);
    },
    isTabletOrDesktop() {
      const width = this.$vuetify.breakpoint.width;
      return width >= 820;
    },
  },
  mounted() {
    this.setEventBus();
  },
  watch: {
    options: {
      handler() {
        this.queueFetch();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    // A pending debounce must not fire on a torn-down component.
    clearTimeout(this.timer);
  },
  methods: {
    queueFetch() {
      // Collapses the burst of watcher fires a page or search change produces
      // into exactly one request.
      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(this.getUsers, 50);
    },
    getUsers() {
      this.loading = true;
      userAPI
        .getClients(this.params, this.searchParams, this.companyId)
        .then((resp) => {
          this.users = resp.data.data;
          this.pagination = resp.data.meta.pagination;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    resetSearch() {
      clearTimeout(this.timer);
      this.search = '';
      this.runSearch();
    },
    runSearch() {
      // Fragment only. getClients already passes params separately; prefixing
      // them here produced a URL with two '?' and lost the page number.
      // Direct matches email exactly and Hub matches it partially (the two
      // UserRepository definitions differ) — name search covers partials on both.
      if (this.search) {
        const q = encodeURIComponent(this.search);
        this.searchParams =
          '&search=first_name:' + q + ';last_name:' + q + ';email:' + q;
      } else {
        this.searchParams = '';
      }
      // One fetch, whatever happens. Resetting the page makes the deep watcher
      // fire, and Vuetify can emit more than one options update per change, so
      // both paths go through the same queue rather than racing each other.
      this.options.page = 1;
      this.queueFetch();
    },
    setEventBus() {
      EventBus.$on('USERS_UPDATED', (payload) => {
        this.params = payload;
        this.getUsers();
      });
    },
    applySearch() {
      // Debounced so a search fires once the admin stops typing, not per keystroke.
      clearTimeout(this.timer);
      this.timer = setTimeout(this.runSearch, 1000);
    },
    showEditPanel(id) {
      this.$showPanel({
        component: EditUser,
        props: {
          id: id,
          params: this.params,
        },
        openOn: 'right',
      });
    },
    sendWelcomeEmail(id) {
      this.$snotify.confirm(this.confirmWelcomeEmail, {
        backdrop: 0.3,
        buttons: [
          {
            text: 'Cancel',
            className: 'toast-cancel-button',
            action: (toast) => {
              this.$snotify.remove(toast.id);
              setTimeout(() => {
                this.$gf.removeBackdrop();
              }, 300);
            },
          },
          {
            text: 'Continue',
            className: 'toast-continue-button',
            action: (toast) => this.processSendWelcomeEmail(toast, id),
          },
        ],
      });
    },
    processSendWelcomeEmail(toast, id) {
      this.$snotify.remove(toast.id);
      userAPI
        .sendWelcomeEmail(id)
        .then(() => {
          this.$snotify.success('The user will receive the welcome email shortly.');
          setTimeout(() => {
            this.$gf.removeBackdrop();
          }, 300);
        })
        .catch(() => {
          this.$snotify.error('The welcome email could not be sent. Please try again.');
          setTimeout(() => {
            this.$gf.removeBackdrop();
          }, 300);
        });
    },
  },
};
</script>
<style scoped>
.company-email{
  font-size: 11px;
}

</style>